import React, { useState, useEffect } from 'react';
import { InstallPrompt } from './InstallPrompt';
import { UpdatePrompt } from './UpdatePrompt';
import { NotificationPrompt } from './NotificationPrompt';
import { OfflineIndicator } from './OfflineIndicator';
import { usePWA } from '../../hooks/usePWA';

interface PWAManagerProps {
  children?: React.ReactNode;
  showInstallPrompt?: boolean;
  showNotificationPrompt?: boolean;
  showOfflineIndicator?: boolean;
  compactPrompts?: boolean;
}

export const PWAManager: React.FC<PWAManagerProps> = ({
  children,
  showInstallPrompt = true,
  showNotificationPrompt = true,
  showOfflineIndicator = true,
  compactPrompts = false
}) => {
  const {
    isInstalled,
    canInstall,
    updateAvailable,
    notificationPermission,
    requestNotifications
  } = usePWA();
  
  const [showInstall, setShowInstall] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(false);
  const [notificationsDismissed, setNotificationsDismissed] = useState(false);

  useEffect(() => {
    // Show install prompt after a delay if not installed and not dismissed
    if (showInstallPrompt && canInstall && !isInstalled && !installDismissed) {
      const timer = setTimeout(() => {
        setShowInstall(true);
      }, 5000); // Show after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [showInstallPrompt, canInstall, isInstalled, installDismissed]);

  useEffect(() => {
    // Show notification prompt after install or after delay
    if (showNotificationPrompt && notificationPermission === 'default' && !notificationsDismissed) {
      const timer = setTimeout(() => {
        setShowNotifications(true);
      }, isInstalled ? 2000 : 15000); // Show 2s after install, or 15s after load

      return () => clearTimeout(timer);
    }
  }, [showNotificationPrompt, notificationPermission, notificationsDismissed, isInstalled]);

  const handleInstallDismiss = () => {
    setShowInstall(false);
    setInstallDismissed(true);
    
    // Show notification prompt after dismissing install
    if (showNotificationPrompt && notificationPermission === 'default') {
      setTimeout(() => setShowNotifications(true), 1000);
    }
  };

  const handleNotificationsDismiss = () => {
    setShowNotifications(false);
    setNotificationsDismissed(true);
  };

  const handleNotificationsEnabled = () => {
    setShowNotifications(false);
    setNotificationsDismissed(true);
  };

  return (
    <>
      {children}
      
      {/* Offline indicator */}
      {showOfflineIndicator && <OfflineIndicator />}
      
      {/* Install prompt */}
      <InstallPrompt
        show={showInstall}
        onDismiss={handleInstallDismiss}
        compact={compactPrompts}
      />
      
      {/* Update prompt */}
      <UpdatePrompt
        compact={compactPrompts}
      />
      
      {/* Notification prompt */}
      <NotificationPrompt
        show={showNotifications}
        onDismiss={handleNotificationsDismiss}
        onEnable={handleNotificationsEnabled}
        compact={compactPrompts}
      />
    </>
  );
};

// Context for PWA state
import { createContext, useContext } from 'react';

interface PWAContextType {
  isInstalled: boolean;
  canInstall: boolean;
  isOnline: boolean;
  updateAvailable: boolean;
  notificationPermission: NotificationPermission;
  installApp: () => Promise<boolean>;
  updateApp: () => Promise<void>;
  requestNotifications: () => Promise<NotificationPermission>;
  syncOfflineData: () => Promise<boolean>;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

export const PWAProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pwaState = usePWA();

  return (
    <PWAContext.Provider value={pwaState}>
      {children}
    </PWAContext.Provider>
  );
};

export const usePWAContext = () => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('usePWAContext must be used within a PWAProvider');
  }
  return context;
};

// Higher-order component for PWA features
export const withPWA = <P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> => {
  return (props: P) => (
    <PWAProvider>
      <PWAManager>
        <Component {...props} />
      </PWAManager>
    </PWAProvider>
  );
};

export default PWAManager;